import { el } from "../util/dom";
import { register } from "../api/auth";
import { ApiError } from "../api/client";
import { buildPasswordField } from "./passwordField";

/** Client-side checks before POST /v1/auth/register (the server enforces
 * the same 8-char minimum). Returns the error copy to show, or null. */
export function validateRegistration(email: string, password: string): string | null {
  const trimmed = email.trim();
  if (!trimmed || !trimmed.includes("@") || trimmed.startsWith("@") || trimmed.endsWith("@")) {
    return "Enter a valid email address.";
  }
  if (password.length < 8) return "Password must be at least 8 characters.";
  return null;
}

export interface RegisterView {
  root: HTMLElement;
}

/** SW-24: the registration screen (/register). Owns its own fields, its
 * validation and its error copy; the login form only links here. On success
 * the token is stored (api/auth.ts register()) and `onRegistered` runs --
 * main.ts refreshes the account screen and navigates to it. */
export function buildRegisterView(onRegistered: () => void): RegisterView {
  const emailInput = el("input", {
    type: "email",
    placeholder: "email",
    class: "auth-input",
    autocomplete: "email",
  }) as HTMLInputElement;
  const { wrap: passwordWrap, input: passwordInput } = buildPasswordField("password (min 8 chars)");
  passwordInput.setAttribute("autocomplete", "new-password");

  // First-party marketing opt-in (unchecked by default -- consent must be an
  // explicit, affirmative choice). Sent as marketing_opt_in.
  const marketingCheckbox = el("input", {
    type: "checkbox",
    class: "auth-checkbox",
    id: "mk-optin",
  }) as HTMLInputElement;
  const marketingLabel = el("label", { class: "auth-checkbox-label", for: "mk-optin" }, [
    "Email me occasional BitClock product updates (optional — you can unsubscribe anytime)",
  ]);

  const errorEl = el("p", { class: "auth-error" });
  const submitBtn = el("button", { class: "auth-btn" }, ["Create account"]) as HTMLButtonElement;

  async function submit(): Promise<void> {
    const email = emailInput.value.trim();
    const password = passwordInput.value;
    const problem = validateRegistration(email, password);
    errorEl.textContent = problem ?? "";
    if (problem) return;
    submitBtn.disabled = true;
    try {
      await register({ email, password, marketing_opt_in: marketingCheckbox.checked });
      passwordInput.value = "";
      onRegistered();
    } catch (err) {
      errorEl.textContent =
        err instanceof ApiError ? err.detail : err instanceof Error ? err.message : String(err);
    } finally {
      submitBtn.disabled = false;
    }
  }
  submitBtn.addEventListener("click", () => void submit());
  passwordInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void submit();
  });

  const root = el("div", { class: "auth-panel" }, [
    el("div", { class: "section-title" }, ["Create your account"]),
    el("div", { class: "auth-row" }, [emailInput]),
    el("div", { class: "auth-row" }, [passwordWrap]),
    el("div", { class: "auth-row auth-optin-row" }, [marketingCheckbox, marketingLabel]),
    el("div", { class: "auth-row" }, [submitBtn]),
    errorEl,
    el("div", { class: "auth-row auth-row-forgot" }, [
      el("a", { class: "auth-link", href: "/account", "data-route": "" }, [
        "Already have an account? Log in",
      ]),
    ]),
  ]);

  return { root };
}
